/**
 * One member, as the committee sees them.
 *
 * This is the only screen in the app that shows an emergency phone number,
 * and it is admin-only. The number is a `tel:` link, because the moment it
 * matters someone is standing on a road trying to dial it. SPEC.md §3.10.
 */

import { useNavigate, useParams } from 'react-router-dom';
import {
  ageFrom,
  EXPECT_VOL,
  formatDate,
  formatTime,
  isPersonalBest,
  memberResults,
  payableUi,
  telHref,
  volunteerHours,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { MyBib, PbGrid } from '../../components/Bib';
import { RaceRow } from '../../components/EventCard';
import { SosScreen } from '../../components/Emergency';
import { PhotoBox } from '../../components/PhotoBox';
import { MemberSheet } from '../../components/sheets/MemberSheet';
import { MemberCreatedSheet } from '../../components/sheets/MemberCreatedSheet';
import { PayLinkSheet, RecordPaymentSheet } from '../../components/sheets/MoneySheets';
import {
  MessageMemberSheet,
  RemoveMemberSheet,
  ResultSheet,
  VolunteerHoursSheet,
} from '../../components/sheets/ClubSheets';
import { BackButton, Button, ButtonRow, Empty, Eyebrow } from '../../components/ui';
import { useGrounds } from '../../context/ClubContext';

export function MemberDetailPage() {
  const grounds = useGrounds();
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { db } = useClubData();
  const { openSheet, openSos, closeSos } = useUI();

  const member = db.members.find((m) => m.id === memberId);

  if (!member) {
    return (
      <>
        <BackButton onClick={() => navigate('/a/members')}>All members</BackButton>
        <Empty title="Member not found">They may have left the club.</Empty>
      </>
    );
  }

  const age = ageFrom(member.dob);
  const isMinor = age !== null && age < 18;
  const year = String(new Date().getFullYear());
  const hours = volunteerHours(db.volunteer, member.id, year);
  const races = memberResults(db.results, member.id);
  const nextBib = db.members.reduce((max, m) => Math.max(max, m.bib), 0) + 1;

  /*
   * Whether the renewal chase makes sense right now — the same payableUi()
   * that runs the member's own Renew button (owner's direction, 17 Aug 2026:
   * a paid member's "Send payment link" is DISABLED until a week before
   * expiry; a life member never sees it at all). Recording cash stays always
   * available: money that actually arrived must always be enterable.
   */
  const pay = payableUi(member);

  return (
    <>
      <BackButton onClick={() => navigate('/a/members')}>All members</BackButton>

      {member.status === 'pending' && (
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3 border border-indigo-200 bg-indigo-50 px-4 py-3.5">
          <div>
            <div className="font-market text-[15px] font-bold tracking-[-0.015em] text-indigo-900">
              Awaiting activation
            </div>
            <div className="mt-0.5 font-market text-[10.5px] text-copy-muted">
              Not on the roster yet — take the joining payment and activate them from Requests
            </div>
          </div>
          <Button tone="d" small onClick={() => navigate('/a/requests')}>
            Open requests
          </Button>
        </div>
      )}

      <MyBib member={member} results={db.results} showDues />

      <div className="mb-4">
        <ButtonRow>
          {pay.show && (
            <Button
              tone="p"
              disabled={!pay.enabled}
              title={pay.hint ?? undefined}
              onClick={() => openSheet(<PayLinkSheet member={member} />)}
            >
              Send payment link
            </Button>
          )}
          <Button tone="g" onClick={() => openSheet(<RecordPaymentSheet member={member} />)}>
            Record payment
          </Button>
          <Button
            tone="d"
            onClick={() => openSheet(<ResultSheet db={db} presetMemberId={member.id} />)}
          >
            + Race result
          </Button>
          <Button
            tone="v"
            onClick={() => openSheet(<VolunteerHoursSheet db={db} presetMemberId={member.id} />)}
          >
            + Volunteer hours
          </Button>
          <Button tone="g" onClick={() => openSheet(<MessageMemberSheet member={member} />)}>
            Message
          </Button>
          <Button
            tone="g"
            onClick={() => openSheet(<MemberSheet member={member} nextBib={nextBib} />)}
          >
            Edit
          </Button>
          <Button
            tone="g"
            onClick={() => openSheet(<MemberCreatedSheet memberId={member.id} />)}
          >
            Sign-in details
          </Button>
          <Button
            tone="r"
            onClick={() => openSos(<SosScreen member={member} onClose={closeSos} />)}
          >
            Emergency card
          </Button>
        </ButtonRow>
      </div>

      {/* Removal sits apart from the row above, and only for members still on
          the roster. Grouping a destructive action with six routine ones is
          how it gets clicked by accident at 6 AM. */}
      {member.status !== 'left' && (
        <div className="mb-4">
          <ButtonRow>
            <Button
              tone="g"
              onClick={() =>
                openSheet(
                  <RemoveMemberSheet member={member} onRemoved={() => navigate('/a/members')} />,
                )
              }
            >
              Remove from club
            </Button>
          </ButtonRow>
        </div>
      )}

      <PhotoBox member={member} />

      {/* Hairline-separated facts grid — the gap-px trick draws the grid lines. */}
      <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden border border-line bg-line min-[560px]:grid-cols-3">
        <FactCell k="Phone" v={member.phone || '—'} mono />
        <FactCell
          k="Email"
          v={
            member.email || (
              /* No email = no password recovery. Worth flagging: this member
                 can lock themselves out permanently. */
              <span className="text-flag-600">Not on file</span>
            )
          }
          mono
        />
        <FactCell k="Age" v={`${age ?? '—'}${isMinor ? ' · minor' : ''}`} mono />
        <FactCell k="Blood group" v={member.blood || '—'} mono />
        <FactCell
          k="Emergency"
          v={
            member.emPhone ? (
              <a
                href={telHref(member.emPhone)}
                className="font-semibold text-flag-600 no-underline hover:underline"
              >
                {member.emName || 'Contact'} · {member.emPhone}
              </a>
            ) : (
              <span className="text-flag-600">Not on file</span>
            )
          }
        />
        {isMinor && (
          <FactCell
            k="Guardian"
            v={
              <>
                {member.guardianName || '—'}
                <br />
                <span className="font-market text-[11px] tabular-nums text-copy-muted">
                  {member.guardianPhone ?? ''}
                </span>
              </>
            }
          />
        )}
        <FactCell k="Member since" v={formatDate(member.joined)} mono />
      </div>

      <Eyebrow>Personal bests</Eyebrow>
      <PbGrid memberId={member.id} results={db.results} />

      <Eyebrow>Volunteer · {year}</Eyebrow>
      <div className="border border-line border-t-[3px] border-t-sea-600 bg-white p-4">
        <div className="font-market text-[34px] font-extrabold leading-none tabular-nums text-sea-600">
          {hours}
          <small className="text-[15px] font-bold text-copy-muted"> / {EXPECT_VOL} hrs</small>
        </div>
        <div className="mb-1.5 mt-3 h-2 overflow-hidden rounded-full bg-line-chip">
          {/* Width comes from data — the one place inline style is right. */}
          <i
            className="block h-full rounded-full bg-sea-600 transition-[width] duration-500"
            style={{ width: `${Math.min(100, (hours / EXPECT_VOL) * 100)}%` }}
          />
        </div>
      </div>

      <Eyebrow>Race history</Eyebrow>
      {races.length ? (
        races.map((r) => (
          <RaceRow
            key={r.id}
            race={r.race}
            meta={`${formatDate(r.date)} · ${r.dist}`}
            time={formatTime(r.timeS)}
            isPb={isPersonalBest(db.results, member.id, r.dist, r.timeS, r.id)}
          />
        ))
      ) : (
        <Empty>No races logged.</Empty>
      )}

      {/* Home ground sits at the foot rather than in the facts grid — it is
          context, not something the committee acts on. */}
      <div className="mt-5 text-center font-market text-[10px] text-copy-label">
        Home ground · {grounds[member.ground] ?? '—'}
      </div>
    </>
  );
}

/** One label/value cell in the facts grid. Values may be JSX. */
function FactCell({ k, v, mono }) {
  return (
    <div className="bg-white px-3.5 py-3">
      <div className="font-market text-[8.5px] uppercase tracking-[0.1em] text-copy-label">{k}</div>
      <div className={`mt-1 text-[13.5px] font-medium ${mono ? 'font-market tabular-nums' : ''}`}>
        {v}
      </div>
    </div>
  );
}
